import React from 'react';
import { Card, CardContent } from "@/ui";
import { Shield, Lock, FileText, Globe, Eye } from 'lucide-react';

const PRIMARY_BLUE = "#1356a6";

export default function PrivacyPolicy() {
  return (
    <div
      className="min-h-screen font-sans"
      style={{
        backgroundImage: "url('/assets/shared/simple-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        fontFamily: "Montserrat, system-ui, sans-serif",
      }}
    >
      <main className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[84rem] mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-full bg-blue-50 shadow-sm">
                <Shield className="h-8 w-8" style={{ color: PRIMARY_BLUE }} />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-normal mb-4" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-600" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
              <strong>Effective Date:</strong> January 8, 2026
            </p>
          </div>

          {/* Introduction */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus Inc ("AIscendus," "Company," "we," "our," or "us") provides electronic medical record (EMR) and healthcare technology software solutions to healthcare organizations. This Privacy Policy describes how we collect, use, disclose, and protect information through our website, software platforms, and related services (collectively, the "Services").
              </p>
            </CardContent>
          </Card>

          {/* Section 1: Information We Collect */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <FileText className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  1. Information We Collect
                </h2>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    a. Information You Provide
                  </h3>
                  <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    We may collect information you voluntarily provide, including:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    <li>Name</li>
                    <li>Company or organization name</li>
                    <li>Job title</li>
                    <li>Email address</li>
                    <li>Phone number</li>
                    <li>Account credentials</li>
                    <li>Customer support communications</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    b. Customer Data and Protected Health Information (PHI)
                  </h3>
                  <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    When healthcare providers use AIscendus' EMR software, we may process <strong>Protected Health Information (PHI)</strong> and other sensitive data on their behalf.
                  </p>
                  <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    In these circumstances:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    <li>AIscendus acts as a <strong>Business Associate</strong> under the Health Insurance Portability and Accountability Act (HIPAA)</li>
                    <li>Our customers act as <strong>Covered Entities</strong> or <strong>Data Controllers</strong></li>
                    <li>AIscendus processes PHI solely to provide the Services and as permitted by law and applicable agreements</li>
                  </ul>
                  <p className="text-base leading-relaxed text-gray-700 mt-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    AIscendus does <strong>not</strong> use PHI for advertising, marketing, or any unauthorized purpose.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    c. Automatically Collected Information
                  </h3>
                  <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    We may automatically collect:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    <li>IP address</li>
                    <li>Device and browser type</li>
                    <li>Log files and usage data</li>
                    <li>Date and time of access</li>
                  </ul>
                  <p className="text-base leading-relaxed text-gray-700 mt-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    This information is used for security, analytics, troubleshooting, and system optimization.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 2: How We Use Information */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Eye className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  2. How We Use Information
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                We use information to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Provide, operate, and maintain the Services</li>
                <li>Create and manage customer accounts</li>
                <li>Deliver customer support and technical assistance</li>
                <li>Improve functionality, reliability, and security</li>
                <li>Communicate service updates and administrative notices</li>
                <li>Comply with legal and regulatory requirements</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 3: HIPAA Compliance */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Shield className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  3. HIPAA Compliance
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus complies with HIPAA and applicable state healthcare privacy laws. When acting as a Business Associate, AIscendus:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Executes <strong>Business Associate Agreements (BAAs)</strong> with customers</li>
                <li>Uses and discloses PHI only as permitted by HIPAA and customer instructions</li>
                <li>Implements safeguards required by the HIPAA Privacy and Security Rules</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700 mt-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Patients seeking access to or correction of medical records should contact their healthcare provider directly.
              </p>
            </CardContent>
          </Card>

          {/* Section 4: HIPAA Security Rule Safeguards */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Lock className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  4. HIPAA Security Rule Safeguards
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-6" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus implements safeguards consistent with the HIPAA Security Rule to protect electronic PHI (ePHI):
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    Administrative Safeguards
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    <li>Information security policies and procedures</li>
                    <li>Workforce security and access controls</li>
                    <li>Security training and awareness programs</li>
                    <li>Ongoing risk assessment and risk management</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    Physical Safeguards
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    <li>Controlled facility access</li>
                    <li>Secure data center environments</li>
                    <li>Device and media management procedures</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    Technical Safeguards
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                    <li>Encryption of data in transit and at rest</li>
                    <li>Unique user identification and authentication</li>
                    <li>Audit logs and monitoring</li>
                    <li>Automatic session timeouts and access controls</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 5: SOC 2 and ISO 27001 Alignment */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Shield className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  5. SOC 2 and ISO 27001 Alignment
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-6" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus maintains a formal information security program designed to protect the confidentiality, integrity, and availability of customer data.
              </p>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Our security controls are aligned with recognized industry standards, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-6" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li><strong>SOC 2 Trust Services Criteria</strong> (Security, Availability, Confidentiality)</li>
                <li><strong>ISO/IEC 27001 Information Security Management System (ISMS)</strong> principles</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Security measures include:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Role-based access controls and least-privilege principles</li>
                <li>Multi-factor authentication</li>
                <li>Continuous monitoring and vulnerability management</li>
                <li>Incident response and breach notification procedures</li>
                <li>Regular security training for employees</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700 mt-6" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Audit reports or certifications may be made available to customers upon request and subject to confidentiality obligations.
              </p>
            </CardContent>
          </Card>

          {/* Section 6: Information Sharing and Disclosure */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Globe className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  6. Information Sharing and Disclosure
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus does <strong>not sell</strong> personal information. We may disclose information only:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>As directed by customers and authorized users</li>
                <li>To subcontractors and service providers under confidentiality and security obligations</li>
                <li>To comply with applicable laws, regulations, or legal processes</li>
                <li>To protect the rights, safety, or security of AIscendus, our customers, or users</li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 7: Data Retention */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <FileText className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  7. Data Retention
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus retains information only for as long as necessary to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Provide the Services</li>
                <li>Meet contractual obligations</li>
                <li>Comply with legal and regulatory requirements</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700 mt-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                PHI retention and deletion are governed by customer agreements and applicable law.
              </p>
            </CardContent>
          </Card>

          {/* Section 8: Cookies and Analytics */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Eye className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  8. Cookies and Analytics
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700 mb-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Our website uses cookies and similar technologies to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <li>Enable essential functionality</li>
                <li>Analyze website usage</li>
                <li>Improve user experience</li>
              </ul>
              <p className="text-base leading-relaxed text-gray-700 mt-3" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                You may control cookies through your browser settings; however, disabling cookies may limit certain features.
              </p>
            </CardContent>
          </Card>

          {/* Section 9: Third-Party Services and Integrations */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Globe className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  9. Third-Party Services and Integrations
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                The Services may integrate with third-party platforms at the direction of customers. AIscendus is not responsible for the privacy practices of third-party services. Data sharing is governed by customer authorization and applicable agreements.
              </p>
            </CardContent>
          </Card>

          {/* Section 10: International Data Transfers */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Globe className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  10. International Data Transfers
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                If you access the Services from outside the United States, you acknowledge that information may be transferred to and processed in the United States or other jurisdictions with different data protection laws.
              </p>
            </CardContent>
          </Card>

          {/* Section 12: Changes to This Privacy Policy */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <FileText className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  12. Changes to This Privacy Policy
                </h2>
              </div>
              <p className="text-base leading-relaxed text-gray-700" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                AIscendus may update this Privacy Policy from time to time. Changes will be posted with a revised effective date.
              </p>
            </CardContent>
          </Card>

          {/* Section 13: Contact Information */}
          <Card className="border-2 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2">
                <div className="p-2 rounded-lg bg-blue-50 shadow-sm">
                  <Shield className="h-6 w-6" style={{ color: PRIMARY_BLUE }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ color: PRIMARY_BLUE, fontFamily: "Montserrat, system-ui, sans-serif" }}>
                  13. Contact Information
                </h2>
              </div>
              <div className="space-y-2" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                <p className="text-base font-semibold text-gray-900">
                  <strong>AIscendus Inc</strong>
                </p>
                <p className="text-base text-gray-700">
                  Email: <a href="mailto:info@aiscendus.ai" className="text-blue-600 hover:underline">info@aiscendus.ai</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

